import React from "react"
import { Slot } from "@radix-ui/react-slot"

import { IKTRichText, TResolvedImage } from "@/types/kontent.types"
import { cn } from "@/lib/utils"
import { RichTextSync, Typography } from "@/components/ui/atoms"
import { Picture } from "@/components/ui/molecules"

export interface IImageOverlapTextProps {
  asChild?: boolean
  reverse?: boolean
  title: string
  body: IKTRichText
  image: TResolvedImage | null
  tagLabel?: string
}

const ImageOverlapText = React.forwardRef<
  HTMLDivElement,
  IImageOverlapTextProps
>(
  (
    {
      asChild = false,
      reverse = false,
      title,
      tagLabel,
      image,
      body,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    return (
      <Component
        className="image-overlap-text mb-12 mt-14 overflow-x-hidden lg:mb-16"
        ref={ref}
        {...props}
      >
        <div className="container relative">
          <div
            className={cn(
              "absolute bottom-0 hidden h-[173px] w-[50rem] bg-secondary-500 lg:block xl:h-[232px] 2xl:h-[282px]",
              reverse
                ? "translate-x-full 2xl:translate-x-[135%]"
                : "lg:translate-x-[-51%]"
            )}
          ></div>
          <div
            className={cn(
              "flex flex-wrap items-end",
              reverse ? "lg:flex-row-reverse" : "lg:flex-row"
            )}
          >
            <div className="hidden lg:block lg:w-2/12"></div>
            <div className="relative w-full lg:w-3/12">
              <div
                className={cn(
                  "absolute bottom-0 h-[173px] w-[70vw] bg-secondary-500 lg:hidden",
                  reverse ? "translate-x-[51%]" : "translate-x-[-51%]"
                )}
              ></div>
              <div
                className={cn(
                  "container flex lg:px-0",
                  reverse ? "flex-row-reverse" : ""
                )}
              >
                <div className="w-3/12 lg:hidden"></div>
                {image?.width && image?.height ? (
                  <Picture
                    src={image.src + "?h=1280&w=1200&fm=webp&q=80"}
                    alt={image.alt}
                    width={image.width}
                    height={image.height}
                    tagLabel={tagLabel}
                    isSemiCircle={true}
                    alignLeft={!reverse}
                    className={cn("z-10 w-9/12 lg:w-full")}
                  />
                ) : null}
              </div>
            </div>
            <div className="lg:w-1/12"></div>
            <div className="container mt-8 lg:mt-0 lg:w-6/12 lg:px-0">
              <Typography as="h2" variant="headingM" className="mb-6 md:mb-7">
                {title}
              </Typography>
              {body ? (
                <div className="text-neutral-400">
                  <RichTextSync blockTitle={title} body={body} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Component>
    )
  }
)
ImageOverlapText.displayName = "ImageOverlapText"

export { ImageOverlapText }
